import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { GoX } from 'react-icons/go';
import { LuBot } from 'react-icons/lu';

import {
  AGENT_PERMISSIONS, agentDraft, customAgentBlock, effectiveAgent, resolvableHarnesses, tickLabels,
} from '../domain/cellWorkflow';

/**
 * Resolves the DOM rect CellWorkflowSection / MissionPanel anchor the popover to, given the graph
 * preview's container and the tapped step's id.
 *
 * The tapped element is the `.mc-workflow-agent` BUTTON, but cells.css gives it `display: contents`
 * (see its comment there) so the button itself generates no box — `getBoundingClientRect()` on it
 * returns an all-zero rect, which reads to AgentPopover as "anchor at the viewport's top-left
 * corner" rather than "anchor on the chip". The flow-kit tag rendered INSIDE the button (BhFlowTag's
 * `.bh-flow-node-tag` span) is what actually has geometry, so that is what gets measured.
 *
 * Guards against a zero-area rect regardless of source — display:contents recurring, a detached
 * node, anything — so a future break here fails safe (AgentPopover's own documented fallback) rather
 * than silently reopening the popover in a screen corner.
 */
export function chipAnchorRect(container, stepId) {
  const chip = container && container.querySelector(`.mc-workflow-agent[data-step-id="${stepId}"]`);
  const tag = chip && chip.querySelector('.bh-flow-node-tag');
  const rect = tag && tag.getBoundingClientRect();
  return (rect && rect.width > 0 && rect.height > 0) ? rect : null;
}

// Kept clear of every viewport edge the popover is pushed off of, so it never sits flush against one.
const VIEWPORT_MARGIN = 8;
// Gap between the chip and the popover it opens.
const ANCHOR_GAP = 4;

/**
 * AgentPopover — the agent picker, anchored to the chip that opened it.
 *
 * Portalled to `document.body` — the move that lets this be a popover at all. Anchoring it inside
 * the ReactFlow canvas (the original objection to a popover) has it clipped by the canvas's own
 * overflow/stacking context; portalling escapes that entirely, and the popover is positioned purely
 * from `anchorRect` (the tapped chip's own `getBoundingClientRect()`), which travels with it across
 * the portal boundary since CSS layout can no longer relate the two.
 *
 * One component, used by both CellWorkflowSection and MissionPanel — the two panels do not share
 * controls anywhere else, but duplicating this one is exactly how they would drift apart.
 *
 * Closes on picking a row, the ×, a click outside, or Escape — all funneled through `onClose`, which
 * the caller uses to clear whatever state is tracking "which step is picked".
 *
 * Props:
 *   anchorRect    — DOMRect | null — the chip's own rect; falls back to the viewport's top-left
 *                   corner when absent, rather than not rendering, since the popover's OPEN/CLOSED
 *                   state is the caller's (whether it mounts this at all). The popover is then
 *                   CLAMPED into the viewport — see VIEWPORT_MARGIN below.
 *   stepId        — the step this chooses an agent for, named in the header
 *   agents        — resolvableAgents(...) — only entries agentSelection can actually resolve
 *   selectedAlias — '' for no agent, 'custom' for an alias-less block, or a real alias — decides
 *                   which row (if any) carries the check mark
 *   onPick(alias) — a real agent row was chosen
 *   onClose()     — × / Escape / outside click
 *   harnesses     — the RAW `harnesses` palette; the component resolves it itself, and an empty
 *                   vocabulary means the whole custom half does not exist — see below
 *   currentAgent  — the picked step's existing agent block, or null; where the sliders start
 *   onBuild(block)— a slider was RELEASED, producing an alias-less five-field block
 *
 * **The custom half is conditional on the vocabulary.** With no resolvable harness there is no
 * section, no separator, and no width change — the popover is exactly the preset list it was
 * before, which is what makes this inert on an instance whose palette predates the `harnesses` key.
 */

// Where the sliders sit when there is nothing to read them off — no agent on the step, or one the
// vocabulary cannot represent. Index 0 of every scale, and every scale is ordered least-to-most, so
// this is the most conservative agent the palette can describe rather than an arbitrary one.
const DEFAULT_DRAFT = { harness: 0, model: 0, effort: 0, permissions: 0 };

// An index into a list that is always in range. The draft carries indices while the lists under
// them can change (a harness swap re-populates two of the four), so every read goes through this
// rather than trusting the pair to stay in step.
function at(values, index) {
  return Math.min(Math.max(index, 0), values.length - 1);
}

export default function AgentPopover({
  anchorRect, stepId, agents, selectedAlias, onPick, onClose, harnesses, currentAgent, onBuild,
}) {
  const ref = useRef(null);
  const [placement, setPlacement] = useState(null);

  const vocabulary = resolvableHarnesses(harnesses);
  const building = vocabulary.length > 0;
  // Null means the step's block cannot be REPRESENTED on these scales — an unknown harness, or a
  // model its own harness does not list. Not clamped to index 0: sliders resting on `haiku` while
  // the step runs `opus` would state something false about what it will do. Seeded at the defaults
  // and said out loud instead, just below the section header.
  // Resolved through the palette first: before the freeze a step carries `agent: {alias: claude}`,
  // not the five fields, and drafting straight off that reads a reference as an unrecognisable block —
  // which showed the "not in the vocabulary" line on a step plainly running a listed preset.
  const seed = agentDraft(effectiveAgent(currentAgent, agents), harnesses);
  const [draft, setDraft] = useState(() => seed || DEFAULT_DRAFT);
  const adrift = !!currentAgent && seed === null;

  useEffect(() => {
    const onMouseDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) onClose();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [onClose]);

  const rect = anchorRect || { bottom: 0, left: 0, top: 0 };

  // The chip is anchored wherever the graph put it, which for a step on the right of a wide workflow
  // is close enough to the viewport edge that a 220px popover hangs off it — the model/effort column
  // is the first thing lost, and it is the column the choice is actually made on. Measured rather
  // than assumed from the CSS width, so a change to the popover's own styling cannot silently
  // reintroduce the overflow. useLayoutEffect runs before paint, so the clamp never flickers.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const left = Math.max(
      VIEWPORT_MARGIN, Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN),
    );
    // Prefer below the chip; flip above only when below would run off and above actually fits, so a
    // popover taller than the space either way still opens downward rather than over its own chip.
    let top = rect.bottom + ANCHOR_GAP;
    if (top + height > window.innerHeight - VIEWPORT_MARGIN) {
      const above = rect.top - height - ANCHOR_GAP;
      if (above >= VIEWPORT_MARGIN) top = above;
    }
    setPlacement({ top, left });
    // `building` is in the deps because it changes the popover's WIDTH (220px to 280px, via the
    // is-building modifier), and the clamp above is computed from the measured box. A vocabulary
    // that arrives while the popover is already open would otherwise widen it past the right edge
    // with a placement measured against the narrow one.
  }, [rect.left, rect.top, rect.bottom, building]);

  const style = placement
    ? { position: 'fixed', top: placement.top, left: placement.left }
    : { position: 'fixed', top: rect.bottom + ANCHOR_GAP, left: rect.left };

  // The four scales, in the order they are read: which harness, then the two things it alone
  // defines, then the one ladder every harness shares. `models`/`efforts` come off the CURRENT
  // harness — that is the whole reason the harness scale is first.
  const harness = building ? vocabulary[at(vocabulary, draft.harness)] : null;
  const scales = harness ? [
    { key: 'harness', name: 'Harness', values: vocabulary.map(entry => entry.alias) },
    { key: 'model', name: 'Model', values: harness.models },
    { key: 'effort', name: 'Effort', values: harness.efforts },
    { key: 'permissions', name: 'Permissions', values: AGENT_PERMISSIONS },
  ] : [];

  // Moving a slider updates LOCAL state only. onChange fires per input event — continuously through
  // a drag — so a write here would post one save per pixel; the write goes on release, below.
  const move = (key, raw) => {
    const index = Number(raw);
    setDraft(prev => {
      if (key !== 'harness') return { ...prev, [key]: index };
      // A different harness brings its own two lists. CLAMPED into them rather than reset to 0, so
      // somebody on the deepest model of one harness lands on the deepest of the next instead of
      // being dropped to the shallowest every time they compare two harnesses.
      const next = vocabulary[at(vocabulary, index)];
      return {
        harness: index,
        model: Math.min(prev.model, next.models.length - 1),
        effort: Math.min(prev.effort, next.efforts.length - 1),
        permissions: prev.permissions,
      };
    });
  };

  // Release — pointer-up (drag and click-on-track alike) and key-up (the keyboard's equivalent) —
  // is the gesture that MEANS something, so it is the one that writes.
  const release = () => {
    if (!harness || !onBuild) return;
    const block = customAgentBlock(
      harness,
      harness.models[at(harness.models, draft.model)],
      harness.efforts[at(harness.efforts, draft.effort)],
      AGENT_PERMISSIONS[at(AGENT_PERMISSIONS, draft.permissions)],
    );
    if (block) onBuild(block);
  };

  // Which of the two halves the step's agent currently came from. A preset row is named; anything
  // else — a block built here, one built through the API, a step with no agent — has no name to
  // give, and saying so is more use than showing a blank.
  const fromPreset = (agents || []).some(agent => agent.alias === selectedAlias);

  return ReactDOM.createPortal(
    <div className={`mc-agent-popover${building ? ' is-building' : ''}`} style={style} ref={ref}>
      <div className="mc-agent-popover-header">
        <span className="mc-agent-popover-title">{`Agent for ${stepId}`}</span>
        <button type="button" className="mc-agent-popover-close" aria-label="Close" onClick={onClose}>
          <GoX />
        </button>
      </div>
      <div className="mc-agent-popover-list">
        {(agents || []).map(agent => (
          <button
            type="button"
            key={agent.alias}
            className={`mc-agent-popover-row${agent.alias === selectedAlias ? ' is-selected' : ''}`}
            onClick={() => onPick(agent.alias)}
          >
            <LuBot size={11} className="mc-agent-popover-icon" />
            <span className="mc-agent-popover-alias">{agent.alias}</span>
            <span className="mc-agent-popover-meta">{`${agent.model} · ${agent.effort}`}</span>
            {agent.alias === selectedAlias && <span className="mc-agent-popover-check">✓</span>}
          </button>
        ))}
      </div>
      {building && (
        <>
          <div className="mc-agent-popover-sep" />
          <div className="mc-agent-popover-build">
            <div className="mc-agent-popover-build-header">
              <span className="mc-agent-popover-build-title">Custom</span>
              <span className="mc-agent-popover-build-state">
                {fromPreset ? `showing ${selectedAlias}` : 'custom — no alias'}
              </span>
            </div>
            {adrift && (
              <div className="mc-agent-popover-build-note">
                This step&rsquo;s agent is not in the palette&rsquo;s vocabulary. Moving a slider replaces it.
              </div>
            )}
            {scales.map(scale => (
              <div className="mc-agent-popover-scale" key={scale.key}>
                <div className="mc-agent-popover-scale-name">
                  <span>{scale.name}</span>
                  {/* The value IN FULL, here, once — which is what lets every tick below stay short
                      enough to read whole rather than being clipped into ambiguity. */}
                  <span className="mc-agent-popover-scale-value">
                    {scale.values[at(scale.values, draft[scale.key])]}
                  </span>
                </div>
                <div className="mc-agent-popover-ticks">
                  {tickLabels(scale.values).map((label, index) => (
                    <span className="mc-agent-popover-tick" key={scale.values[index]}
                      title={scale.values[index]}>{label}</span>
                  ))}
                </div>
                <input
                  type="range"
                  className="mc-agent-popover-range"
                  aria-label={scale.name}
                  min={0}
                  max={scale.values.length - 1}
                  step={1}
                  value={at(scale.values, draft[scale.key])}
                  onChange={event => move(scale.key, event.target.value)}
                  onPointerUp={release}
                  onKeyUp={release}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>,
    document.body
  );
}
